import { useState } from 'react';
import {
  Bank, Buildings, DeviceMobile, EnvelopeSimple, FloppyDisk, Globe, ListPlus, MapPin, Phone, Tag, User,
} from '@phosphor-icons/react';
import { supabase } from '../../../lib/supabase/client';
import { baueKontaktPayload, pflichtfeldFehler } from '../../../lib/kontakte/formular';
import type { KontaktVorbelegung } from '../../../lib/selbstauskunft/kontakt';
import { FormRahmen } from '../../../components/island/form/FormRahmen';
import { FormAbschnitt } from '../../../components/island/form/FormAbschnitt';
import { FormFeld, FormZeile } from '../../../components/island/form/FormFeld';
import { FormSegment } from '../../../components/island/form/FormSegment';
import { FormAuswahl, type FormAuswahlOption } from '../../../components/island/form/FormAuswahl';
import { FormSchalter } from '../../../components/island/form/FormSchalter';
import { FormKnopfleiste } from '../../../components/island/form/FormKnopfleiste';
import { useFormFehler } from '../../../components/island/form/useFormFehler';
import { fehlerMeldung } from '../../../components/island/form/form-logik';
import { ABSCHNITT_FARBE, ZEILEN_ICON } from '../../../components/island/form/form-stil';

type TabKey = 'privatperson' | 'unternehmen';

// Auswahlen wie bisher im <select>; die leere Option bleibt wählbar („—“ wie im Prototyp).
const KONTAKTARTEN = ['Mieter', 'Eigentümer', 'Dienstleister', 'Handwerker', 'Makler', 'Sonstige'];
const UNTERNEHMENSARTEN = ['Mieter', 'Dienstleister', 'Handwerker', 'Immobilienverwaltung', 'Makler', 'Eigentümer', 'Bank', 'Hausmeister', 'Bekannter', 'Sonstige'];
const LAENDER = ['Deutschland', 'Österreich', 'Schweiz'];

const mitLeer = (werte: readonly string[], leer: string): FormAuswahlOption<string>[] => [
  { wert: '', label: leer },
  ...werte.map(w => ({ wert: w, label: w, icon: Tag, iconFlaeche: ZEILEN_ICON.gedaempft.flaeche, iconFarbe: ZEILEN_ICON.gedaempft.farbe })),
];

// onDone bekommt nach dem Speichern die neue Id (Selbstauskunft merkt sich den Kontakt), beim Abbrechen nichts.
export default function KontaktErstellenFormular({ onDone, initial }: { onDone: (neueId?: string) => void; initial?: KontaktVorbelegung & { firmenname?: string } }) {
  // Aus einer E-Mail vorbelegt: sieht der Absendername nach einer Firma aus, startet das
  // Formular gleich im Unternehmen-Tab — sonst landet "Meyer & Sohn GmbH" im Nachnamen.
  const [activeTab, setActiveTab] = useState<TabKey>(initial?.firmenname ? 'unternehmen' : 'privatperson');
  const [erweitert, setErweitert] = useState(true);
  const [saving, setSaving] = useState(false);
  // Pflichtfeld- und Serverfehler: Leiste oben + rotes Feld (gleiche Texte wie bisher)
  const fehler = useFormFehler();

  // Privatperson
  const [anrede, setAnrede] = useState(initial?.anrede ?? '');
  const [vorname, setVorname] = useState(initial?.vorname ?? '');
  const [nachname, setNachname] = useState(initial?.nachname ?? '');
  const [kontaktart, setKontaktart] = useState(initial?.kontaktart ?? '');
  const [geburtsdatum, setGeburtsdatum] = useState(initial?.geburtsdatum ?? '');

  // Unternehmen
  const [unternehmensart, setUnternehmensart] = useState('');
  const [firmenname, setFirmenname] = useState(initial?.firmenname ?? '');

  // Adresse
  const [strasse, setStrasse] = useState(initial?.strasse ?? '');
  const [hausnummer, setHausnummer] = useState(initial?.hausnummer ?? '');
  const [plz, setPlz] = useState(initial?.plz ?? '');
  const [stadt, setStadt] = useState(initial?.stadt ?? '');
  const [land, setLand] = useState('');

  // Kontaktmöglichkeiten
  const [email, setEmail] = useState(initial?.email ?? '');
  const [festnetz, setFestnetz] = useState(initial?.festnetz ?? '');
  const [handynummer, setHandynummer] = useState(initial?.handynummer ?? '');

  // Bankdaten
  const [kontoinhaber, setKontoinhaber] = useState('');
  const [bank, setBank] = useState('');
  const [iban, setIban] = useState('');
  const [bic, setBic] = useState('');
  const [steuernummer, setSteuernummer] = useState('');
  const [ustIdNr, setUstIdNr] = useState('');
  const [glaeubigerId, setGlaeubigerId] = useState('');

  const handleSave = async () => {
    const pflicht = pflichtfeldFehler({ activeTab, nachname, firmenname });
    if (pflicht) fehler.zeige(pflicht.text, [pflicht.feld]); else fehler.leeren();
    if (pflicht) return;

    setSaving(true);
    try {
      const payload = baueKontaktPayload({
        activeTab, anrede, vorname, nachname, kontaktart, geburtsdatum,
        unternehmensart, firmenname, strasse, hausnummer, plz, stadt, land,
        email, festnetz, handynummer, kontoinhaber, bank, iban, bic,
        steuernummer, ustIdNr, glaeubigerId,
      });
      const { data, error } = await supabase.from('kontakte').insert(payload).select('id').single();
      if (error) throw error;
      onDone(data?.id);
    } catch (err: unknown) {
      fehler.zeige('Fehler: ' + fehlerMeldung(err, 'Unbekannter Fehler'));
    } finally {
      setSaving(false);
    }
  };

  const privat = activeTab === 'privatperson';
  const erweitertSchalter = (
    <FormSchalter text="Erweiterte Kontaktinformationen einblenden?" an={erweitert} onChange={() => setErweitert(!erweitert)}
      icon={ListPlus} verlauf={ABSCHNITT_FARBE.violett.verlauf} />
  );

  return (
    <FormRahmen fehler={fehler.text} knopfleiste={
      <FormKnopfleiste abbrechen={{ label: 'Abbrechen', onClick: () => onDone() }}
        haupt={{ label: 'Kontakt erstellen', icon: FloppyDisk, typ: 'button', onClick: handleSave, laedt: saving }} />
    }>
      {/* Kontakt / Unternehmensinformationen */}
      <FormAbschnitt titel={privat ? 'Kontakt' : 'Unternehmensinformationen'} icon={privat ? User : Buildings} farbe="violett" index={0}
        pflicht={1} fehlt={fehler.felder.length}>
        <FormSegment label="Art" ohneBeschriftung wert={activeTab}
          optionen={[{ wert: 'privatperson', label: 'Privatperson' }, { wert: 'unternehmen', label: 'Unternehmen' }]}
          onChange={(tab) => { setActiveTab(tab); fehler.leeren(); }} />

        {privat ? (
          <>
            <FormSegment label="Anrede" wert={anrede}
              optionen={['Herr', 'Frau', 'Divers'].map(a => ({ wert: a, label: a }))} onChange={setAnrede} />
            <FormZeile>
              <FormFeld label="Vorname" value={vorname} onChange={(e) => setVorname(e.target.value)} />
              <FormFeld label="Nachname" value={nachname} pflicht fehler={fehler.istFehler('nachname')}
                onChange={(e) => { setNachname(e.target.value); fehler.leeren(); }} />
            </FormZeile>
            <FormZeile>
              <FormAuswahl label="Kontaktart" optionen={mitLeer(KONTAKTARTEN, '—')} wert={kontaktart} onChange={setKontaktart} />
              <FormFeld label="Geburtsdatum" typ="date" value={geburtsdatum} onChange={(e) => setGeburtsdatum(e.target.value)} />
            </FormZeile>
          </>
        ) : (
          <FormZeile>
            <FormAuswahl label="Unternehmensart" optionen={mitLeer(UNTERNEHMENSARTEN, '—')} wert={unternehmensart} onChange={setUnternehmensart} />
            <FormFeld label="Firmenname" value={firmenname} pflicht fehler={fehler.istFehler('firmenname')}
              onChange={(e) => { setFirmenname(e.target.value); fehler.leeren(); }} />
          </FormZeile>
        )}

        {erweitertSchalter}
      </FormAbschnitt>

      {erweitert && (
        <>
          <FormAbschnitt titel="Adresse" icon={MapPin} farbe="rot" index={1}>
            <FormZeile spalten="minmax(0,2fr) minmax(0,1fr)">
              <FormFeld label="Strasse" value={strasse} onChange={(e) => setStrasse(e.target.value)} />
              <FormFeld label="Hausnummer" value={hausnummer} onChange={(e) => setHausnummer(e.target.value)} />
            </FormZeile>
            <FormZeile>
              <FormFeld label="PLZ" value={plz} onChange={(e) => setPlz(e.target.value)} />
              <FormFeld label="Stadt" value={stadt} onChange={(e) => setStadt(e.target.value)} />
            </FormZeile>
            <FormAuswahl label="Land" platzhalter="Wähle eine Option" wert={land} onChange={setLand}
              optionen={[{ wert: '', label: 'Wähle eine Option' }, ...LAENDER.map(l => ({ wert: l, label: l, icon: Globe, iconFlaeche: ZEILEN_ICON.blau.flaeche, iconFarbe: ZEILEN_ICON.blau.farbe }))]} />
          </FormAbschnitt>

          <FormAbschnitt titel="Kontaktmöglichkeiten" icon={Phone} farbe="blau" index={2}>
            <FormFeld label="E-Mail" typ="email" value={email} icon={EnvelopeSimple} onChange={(e) => setEmail(e.target.value)} />
            <FormZeile>
              <FormFeld label="Festnetz" value={festnetz} icon={Phone} onChange={(e) => setFestnetz(e.target.value)} />
              <FormFeld label="Handynummer" value={handynummer} icon={DeviceMobile} onChange={(e) => setHandynummer(e.target.value)} />
            </FormZeile>
          </FormAbschnitt>

          <FormAbschnitt titel="Bankdaten" icon={Bank} farbe="gruen" index={3}>
            <FormZeile>
              <FormFeld label="Kontoinhaber" value={kontoinhaber} onChange={(e) => setKontoinhaber(e.target.value)} />
              <FormFeld label="Bank" value={bank} onChange={(e) => setBank(e.target.value)} />
            </FormZeile>
            <FormZeile>
              <FormFeld label="IBAN" value={iban} onChange={(e) => setIban(e.target.value)} />
              <FormFeld label="BIC" value={bic} onChange={(e) => setBic(e.target.value)} />
            </FormZeile>
            <FormZeile>
              <FormFeld label="Steuernummer" value={steuernummer} onChange={(e) => setSteuernummer(e.target.value)} />
              <FormFeld label="USt-IdNr." value={ustIdNr} onChange={(e) => setUstIdNr(e.target.value)} />
            </FormZeile>
            <FormFeld label="Gläubiger-ID" value={glaeubigerId} onChange={(e) => setGlaeubigerId(e.target.value)} />
          </FormAbschnitt>
        </>
      )}
    </FormRahmen>
  );
}
